// =============================================================================
// FieldGroup — label + input/textarea/select/checkbox with consistent spacing
// =============================================================================
// Props: label, name, value, onChange, type?, options?, error?, readOnly?, helpText?
// type: 'text' (default) | 'textarea' | 'select' | 'checkbox' |
//       'number' | 'date' | 'email' | 'tel' | 'url'
// Changelog:
//   S25 — Wrapped in React.memo to short-circuit re-renders on unrelated
//         field updates (autosave race fix companion change).

import React from 'react';

function FieldGroup({
  label, name, value, onChange,
  type = 'text', options, error, readOnly, helpText, rows,
}) {
  // Checkbox gets a completely different layout — inline label, no top label
  if (type === 'checkbox') {
    return (
      <div>
        <label className="flex items-center gap-2 cursor-pointer" htmlFor={name}>
          <input
            type="checkbox"
            id={name}
            name={name}
            checked={!!value}
            onChange={e => onChange(e.target.checked)}
            disabled={readOnly}
            className="w-4 h-4 accent-mid-green mt-px flex-shrink-0"
          />
          <span className="label-dark cursor-pointer">{label}</span>
        </label>
        {helpText && !error && <p className="text-xs text-silver-mute mt-1">{helpText}</p>}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>
    );
  }

  const inputClass = `input mt-1${error ? ' border-red-400 focus:ring-red-400 focus:border-red-400' : ''}`;

  return (
    <div>
      <label className="label-dark" htmlFor={name}>{label}</label>

      {type === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          readOnly={readOnly}
          rows={rows ?? 2}
          className={`${inputClass} resize-y`}
        />
      ) : type === 'select' ? (
        <select
          id={name}
          name={name}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          disabled={readOnly}
          className={inputClass}
        >
          <option value="">—</option>
          {(options || []).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          name={name}
          type={type}
          value={value ?? ''}
          onChange={e => {
            const v = e.target.value;
            if (type === 'number' && v !== '' && Number(v) < 0) {
              onChange('');
            } else {
              onChange(v);
            }
          }}
          readOnly={readOnly}
          min={type === 'number' ? 0 : undefined}
          className={inputClass}
        />
      )}

      {helpText && !error && (
        <p className="text-xs text-silver-mute mt-1">{helpText}</p>
      )}
      {error && (
        <p className="text-xs text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
}

export default React.memo(FieldGroup);
